// The direction B rail chrome, dark in both modes: logo (the mark alone when collapsed), the collapse
// control on desktop or the close control in the mobile drawer, a scrolling nav slot and an optional
// footer. Shared by the dashboard sidebar and, later, the reviewer area. Props:
//   ...FrameState   from AppFrame: `collapsed`, `toggleCollapsed`, `isDesktop`, `setMobileOpen`
//   logoId          unique per rendered logo (the SVG's gradient ids)
//   brandExtra?     a co-brand under the logo (the Gateway partner logo)
//   footer?         pinned below the nav (the prototype's credit balance slot)
//   onNavScroll?    scroll handler of the nav area
//   children        the nav, usually a <SidebarNavList>
import { PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import type { ReactNode, UIEventHandler } from 'react';

import { LogoLockup } from '@/components/brand/art';
import { Button } from '@/components/lumen/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/lumen/tooltip';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { useHaptic } from '@/utils/haptic';
import type { FrameState } from '../AppFrame';

export interface SidebarRailProps extends FrameState {
  logoId: string;
  brandExtra?: ReactNode;
  footer?: ReactNode;
  onNavScroll?: UIEventHandler<HTMLDivElement>;
  children: ReactNode;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

function RailLogo({ id, collapsed }: { id: string; collapsed: boolean }) {
  // The lockup's mark spans the first 186 of its 838 viewBox units; collapsed, the wrapper crops to it.
  return (
    <span className={cn('block shrink-0 overflow-hidden leading-none', collapsed ? 'w-6.5' : 'w-auto')}>
      <span className="block h-8 [&_svg]:h-full [&_svg]:w-auto">
        <LogoLockup id={id} />
      </span>
    </span>
  );
}

export function SidebarRail({
  collapsed,
  toggleCollapsed,
  isDesktop,
  setMobileOpen,
  logoId,
  brandExtra,
  footer,
  onNavScroll,
  children,
}: SidebarRailProps) {
  const { t } = useLanguage();
  const hapticFirm = useHaptic('firm');
  const hapticLight = useHaptic('light');

  const toggleLabel = t(collapsed ? 'sidebar.expand' : 'sidebar.collapse');
  const shortcut = isMac ? '⌘B' : 'Ctrl+B';

  const control = isDesktop ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => {
            hapticFirm();
            toggleCollapsed();
          }}
          aria-label={toggleLabel}
          aria-keyshortcuts={isMac ? 'Meta+B' : 'Control+B'}
          className="focus-visible:ring-2 focus-visible:ring-label"
        >
          {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {toggleLabel}
        <kbd className="ml-2 font-mono opacity-70">{shortcut}</kbd>
      </TooltipContent>
    </Tooltip>
  ) : (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={() => {
        hapticLight();
        setMobileOpen(false);
      }}
      aria-label={t('lumen.close')}
      className="focus-visible:ring-2 focus-visible:ring-label"
    >
      <X />
    </Button>
  );

  return (
    <aside
      data-slot="sidebar-rail"
      data-collapsed={collapsed || undefined}
      className="lm-dark flex h-full w-full flex-col gap-3 overflow-hidden border-line border-r bg-canvas pt-4 pb-3 text-fg"
    >
      <div
        className={cn(
          'flex shrink-0',
          collapsed ? 'flex-col items-center gap-3 px-3' : 'items-center justify-between gap-2 pr-3 pl-4'
        )}
      >
        <RailLogo id={logoId} collapsed={collapsed} />
        {control}
      </div>
      {brandExtra && <div className={cn('shrink-0', collapsed ? 'px-3' : 'px-4')}>{brandExtra}</div>}
      <div
        data-slot="sidebar-rail-nav"
        onScroll={onNavScroll}
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-3"
      >
        {children}
      </div>
      {footer && <div className="shrink-0 px-3">{footer}</div>}
    </aside>
  );
}
